import React from 'react';
import { Button } from '../ui/button';
import { ArrowRight, Shield, Globe, CheckCircle, Award, Star, Zap, Lock } from 'lucide-react';

const HeroSection = () => {
  const scrollToApplication = () => {
    const element = document.getElementById('application');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="hero-gradient min-h-screen flex items-center relative overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1737815914093-becb6c9ac064?w=2400&q=80&auto=format&fit=crop"
          alt="Raw gold nuggets from artisanal mining"
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#040F2D]/95 via-[#0B132B]/90 to-[#040F2D]/95"></div>
      </div>
      {/* Enhanced background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-40 h-40 bg-[#D4AF37]/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-60 h-60 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-[#C5A059]/15 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div className="text-white px-2 sm:px-0">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight mt-6 sm:mt-12">
              <span className="text-white block mb-2 sm:mb-4">
                Export Your Gold Legally —
              </span>
              <span className="text-[#D4AF37] block">
                We cover your taxes and buy your gold.
              </span>
            </h1>
            
            <h2 className="text-lg sm:text-xl text-gray-200 leading-relaxed mb-8 sm:mb-12 max-w-2xl font-medium">
              GOLDCIF provides artisanal African miners with a complete legal pathway to access international gold market. Focus on your work, we manage the hurdles.
            </h2>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-12 sm:mb-16">
              <Button
                onClick={scrollToApplication}
                size="lg"
                className="bg-[#D4AF37] hover:bg-[#C5A059] text-white font-bold px-6 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl group border-0 shadow-2xl hover:shadow-3xl transition-all transform hover:scale-105"
              >
                <Award className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6" />
                Become a Verified Seller
                <ArrowRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 group-hover:translate-x-2 transition-transform" />
              </Button>
            </div>
          </div>
          
          <div className="hidden lg:block">
            <div className="relative">
              <div className="w-96 h-96 bg-gradient-to-br from-[#D4AF37]/25 to-transparent rounded-full blur-3xl absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
              {/* Mining team photo above the process card */}
              <div className="relative z-10 mb-6 rounded-2xl overflow-hidden shadow-2xl border-2 border-[#D4AF37]/30">
                <img
                  src="https://images.unsplash.com/photo-1523848309072-c199db53f137?w=1200&q=80&auto=format&fit=crop"
                  alt="Mining operations on site"
                  className="w-full h-56 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#040F2D]/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-white font-bold text-sm flex items-center">
                    <Award className="h-4 w-4 text-[#D4AF37] mr-2" /> Licensed operations · Uganda
                  </p>
                </div>
              </div>
              <div className="relative z-10 bg-white/5 backdrop-blur-xl rounded-3xl p-10 border-2 border-[#D4AF37]/30 shadow-2xl">
                <div className="flex items-center justify-center mb-8">
                  <Zap className="h-8 w-8 text-[#D4AF37] mr-3" />
                  <h3 className="text-2xl font-bold text-white">Simple Process</h3>
                </div>
                <div className="space-y-6">
                  <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-[#D4AF37]/20">
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-bold">1</div>
                    <span className="text-gray-200 font-medium">Apply & Verify</span>
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] ml-auto" />
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-[#D4AF37]/20">
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-bold">2</div>
                    <span className="text-gray-200 font-medium">Test & Export</span>
                    <Shield className="h-5 w-5 text-[#D4AF37] ml-auto" />
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl backdrop-blur-sm border border-[#D4AF37]/20">
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-bold">3</div>
                    <span className="text-gray-200 font-medium">Get Paid</span>
                    <Lock className="h-5 w-5 text-[#D4AF37] ml-auto" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;